import React, { useState, useEffect } from 'react';
import {
  Users, ShieldCheck, CreditCard, TrendingUp, Activity,
  Building2, UserCheck, UserX, RefreshCw, ChevronDown,
  Clock, Package, BarChart3, Ban, PauseCircle, AlertTriangle,
  Gift
} from 'lucide-react';
import { superAdminAPI } from '../../services/api';
import { toast } from 'react-toastify';

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt = (n) => (n ?? 0).toLocaleString();
const pct = (part, total) => (!total ? 0 : Math.round((part / total) * 100));

// ── Stat Card ─────────────────────────────────────────────────────────────────
const StatCard = ({ icon: Icon, label, value, sub, color, badge, badgeCls }) => (
  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow">
    <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${color}`}>
      <Icon size={22} className="text-white" />
    </div>
    <div className="min-w-0 flex-1">
      <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-2xl font-bold text-gray-800">{value}</p>
      {sub && <p className="text-xs text-gray-400 mt-0.5">{sub}</p>}
    </div>
    {badge != null && (
      <div className={`text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0 ${badgeCls}`}>
        {badge}%
      </div>
    )}
  </div>
);

// ── Progress Bar ───────────────────────────────────────────────────────────────
const ProgressBar = ({ label, value, total, color, icon: Icon }) => {
  const width = pct(value, total);
  return (
    <div className="mb-4">
      <div className="flex justify-between items-center mb-1.5">
        <div className="flex items-center gap-1.5">
          {Icon && <Icon size={13} className="text-gray-400" />}
          <span className="text-sm font-medium text-gray-700">{label}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm font-bold text-gray-800">{fmt(value)}</span>
          <span className="text-xs text-gray-400">({width}%)</span>
        </div>
      </div>
      <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
        <div className={`h-full rounded-full transition-all duration-700 ease-out ${color}`} style={{ width: `${width}%` }} />
      </div>
    </div>
  );
};

// ── Section Title ──────────────────────────────────────────────────────────────
const SectionTitle = ({ icon: Icon, title, color = 'text-gray-700', style: extraStyle }) => (
  <div className="flex items-center gap-2 mb-4">
    <Icon size={16} className={color} style={extraStyle} />
    <h2 className={`text-xs font-bold uppercase tracking-wider ${color}`} style={extraStyle}>{title}</h2>
  </div>
);

// ── Org Card ──────────────────────────────────────────────────────────────────
const OrgCard = ({ label, value, total, barColor, bg, textCls }) => (
  <div className={`rounded-xl border p-4 ${bg}`}>
    <div className="flex justify-between items-start mb-2">
      <span className={`text-sm font-semibold ${textCls}`}>{label}</span>
      <span className={`text-xl font-bold ${textCls}`}>{fmt(value)}</span>
    </div>
    <div className="h-1.5 bg-white/60 rounded-full overflow-hidden">
      <div className={`h-full rounded-full ${barColor} transition-all duration-700`} style={{ width: `${pct(value, total)}%` }} />
    </div>
    <p className="text-xs text-gray-400 mt-1.5">{pct(value, total)}% of total</p>
  </div>
);

// ── Main Dashboard ─────────────────────────────────────────────────────────────
const SuperAdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  const primaryColor = { color: "#1C4E6D" };
  const primaryLightBg = { backgroundColor: "#e8f4f9" };
  const freeTrialBadgeCls = { backgroundColor: "#e8f4f9", color: "#1C4E6D" };

  const fetchData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes] = await Promise.allSettled([
        superAdminAPI.getDashboardStats(),
        superAdminAPI.getAllUsers(),
      ]);
      if (statsRes.status === 'fulfilled') setStats(statsRes.value.data);
      if (usersRes.status === 'fulfilled') {
        const data = Array.isArray(usersRes.value.data)
          ? usersRes.value.data
          : usersRes.value.data?.content || [];
        setUsers(data);
      }
      setLastUpdated(new Date());
    } catch {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const latestUsers = [...users]
    .sort((a, b) => new Date(b.createdAt || 0) - new Date(a.createdAt || 0))
    .slice(0, 5);

  const total = stats?.totalUsers ?? 0;

  return (
    <div className="min-h-screen bg-gray-50">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between p-4 sm:p-6 bg-white border-b gap-4 shadow-sm">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-gradient-to-br from-indigo-600 to-purple-600 rounded-xl flex items-center justify-center">
            <ShieldCheck size={18} className="text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-semibold text-gray-900 flex items-center gap-2">
              Dashboard <ChevronDown size={18} className="text-gray-500" />
            </h1>
            <p className="text-xs text-gray-500">
              Platform statistics &amp; overview
              {lastUpdated && <span className="ml-2">· Updated {lastUpdated.toLocaleTimeString()}</span>}
            </p>
          </div>
          {loading && <span className="text-xs text-red-500 animate-pulse">Loading...</span>}
        </div>
        <button
          onClick={fetchData}
          disabled={loading}
          className="flex items-center gap-2 px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm hover:bg-indigo-700 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Refreshing…' : 'Refresh'}
        </button>
      </div>

      {/* ── Loading skeleton ── */}
      {loading && !stats && (
        <div className="p-4 sm:p-6 grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(12)].map((_, i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 animate-pulse h-24" />
          ))}
        </div>
      )}

      {stats && (
        <div className="p-4 sm:p-6 space-y-6">

          {/* ── Section 1: User accounts ── */}
          <div>
            <SectionTitle icon={Users} title="User Accounts" color="text-indigo-600" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={Users} label="Total Users" value={fmt(stats.totalUsers)} color="bg-indigo-500" sub="Registered accounts" />
              <StatCard icon={UserCheck} label="Active Users" value={fmt(stats.activeUsers)} color="bg-green-500"
                badge={pct(stats.activeUsers, total)} badgeCls="bg-green-50 text-green-600" />
              <StatCard icon={Clock} label="Pending Approval" value={fmt(stats.pendingUsers)} color="bg-yellow-500"
                badge={pct(stats.pendingUsers, total)} badgeCls="bg-yellow-50 text-yellow-700" />
              <StatCard icon={UserX} label="Deactivated" value={fmt(stats.deactivatedUsers)} color="bg-red-400"
                badge={pct(stats.deactivatedUsers, total)} badgeCls="bg-red-50 text-red-500" />
            </div>
          </div>

          {/* ── Section 2: Restricted statuses ── */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatCard icon={PauseCircle} label="On Hold" value={fmt(stats.onHoldUsers)} color="bg-orange-400"
              badge={pct(stats.onHoldUsers, total)} badgeCls="bg-orange-50 text-orange-600" />
            <StatCard icon={AlertTriangle} label="Suspended" value={fmt(stats.suspendedUsers)} color="bg-amber-500"
              badge={pct(stats.suspendedUsers, total)} badgeCls="bg-amber-50 text-amber-600" />
            <StatCard icon={Ban} label="Banned" value={fmt(stats.bannedUsers)} color="bg-gray-500"
              badge={pct(stats.bannedUsers, total)} badgeCls="bg-gray-100 text-gray-600" />
            <StatCard icon={Activity} label="Active Rate" value={`${pct(stats.activeUsers, total)}%`}
              color="bg-emerald-500" sub={`${fmt(stats.activeUsers)} of ${fmt(total)} users`} />
          </div>

          {/* ── Section 3: Payment ── */}
          <div>
            <SectionTitle icon={CreditCard} title="Payment Status" color="text-emerald-600" />
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
              <StatCard icon={CreditCard} label="Paid Users" value={fmt(stats.paidUsers)} color="bg-emerald-500"
                badge={pct(stats.paidUsers, total)} badgeCls="bg-emerald-50 text-emerald-600" />

              {/* Free Trial card — color applied via style prop, not className */}
              <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 flex items-center gap-4 hover:shadow-md transition-shadow">
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0" style={primaryLightBg}>
                  <Gift size={22} style={primaryColor} />
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs text-gray-500 font-medium uppercase tracking-wide">Free Trial</p>
                  <p className="text-2xl font-bold text-gray-800">{fmt(stats.freeTrialUsers)}</p>
                </div>
                <div className="text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap flex-shrink-0" style={freeTrialBadgeCls}>
                  {pct(stats.freeTrialUsers, total)}%
                </div>
              </div>

              <StatCard icon={Package} label="Unpaid" value={fmt(stats.unpaidUsers)} color="bg-rose-500"
                badge={pct(stats.unpaidUsers, total)} badgeCls="bg-rose-50 text-rose-600" />
              <StatCard icon={TrendingUp} label="Revenue Rate" value={`${pct(stats.paidUsers, total)}%`}
                color="bg-violet-500" sub="Paid / Total users" />
            </div>
          </div>

          {/* ── Section 4: Org types ── */}
          <div>
            <SectionTitle icon={Building2} title="Organization Types" color="text-gray-700" style={primaryColor} />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <OrgCard label="Corporates" value={stats.corporates} total={total}
                barColor="bg-indigo-500" bg="bg-indigo-50 border-indigo-100" textCls="text-indigo-700" />
              <OrgCard label="Staffing Agencies" value={stats.staffingAgencies} total={total}
                barColor="bg-purple-500" bg="bg-purple-50 border-purple-100" textCls="text-purple-700" />
              <OrgCard label="Freelancers" value={stats.freelancers} total={total}
                barColor="bg-orange-500" bg="bg-orange-50 border-orange-100" textCls="text-orange-700" />
            </div>
          </div>

          {/* ── Section 5: Breakdown charts ── */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <SectionTitle icon={Activity} title="Account Status Breakdown" color="text-indigo-600" />
              <ProgressBar label="Active"      value={stats.activeUsers}      total={total} color="bg-green-500"  icon={UserCheck} />
              <ProgressBar label="Pending"     value={stats.pendingUsers}     total={total} color="bg-yellow-400" icon={Clock} />
              <ProgressBar label="Deactivated" value={stats.deactivatedUsers} total={total} color="bg-red-400"    icon={UserX} />
              <ProgressBar label="On Hold"     value={stats.onHoldUsers}      total={total} color="bg-orange-400" icon={PauseCircle} />
              <ProgressBar label="Suspended"   value={stats.suspendedUsers}   total={total} color="bg-amber-400"  icon={AlertTriangle} />
              <ProgressBar label="Banned"      value={stats.bannedUsers}      total={total} color="bg-gray-400"   icon={Ban} />
            </div>

            <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
              <SectionTitle icon={CreditCard} title="Payment Breakdown" color="text-emerald-600" />
              <ProgressBar label="Paid"       value={stats.paidUsers}      total={total} color="bg-emerald-500" icon={CreditCard} />
              <ProgressBar label="Free Trial" value={stats.freeTrialUsers} total={total} color="bg-blue-400"   icon={Gift} />
              <ProgressBar label="Unpaid"     value={stats.unpaidUsers}    total={total} color="bg-rose-400"   icon={Package} />

              <div className="mt-5 pt-4 border-t border-gray-50 grid grid-cols-3 gap-3 text-center">
                {[
                  { label: 'Paid',       val: `${pct(stats.paidUsers, total)}%`,      cls: 'text-emerald-600', inlineStyle: null },
                  { label: 'Free Trial', val: `${pct(stats.freeTrialUsers, total)}%`, cls: '',                  inlineStyle: primaryColor },
                  { label: 'Unpaid',     val: `${pct(stats.unpaidUsers, total)}%`,    cls: 'text-rose-600',     inlineStyle: null },
                ].map(({ label, val, cls, inlineStyle }) => (
                  <div key={label}>
                    <p className={`text-lg font-bold ${cls}`} style={inlineStyle || {}}>{val}</p>
                    <p className="text-xs text-gray-400">{label}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* ── Recent Signups table ── */}
          {latestUsers.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
              <div className="px-5 py-4 border-b border-gray-50 flex items-center gap-2">
                <Clock size={15} className="text-gray-400" />
                <h2 className="text-xs font-bold uppercase tracking-wider text-gray-700">Recent Signups</h2>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full min-w-max">
                  <thead className="bg-gray-50 border-b border-gray-100">
                    <tr>
                      {['#', 'Organization', 'Email', 'Type', 'Payment', 'Status', 'Joined'].map(h => (
                        <th key={h} className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wider whitespace-nowrap">
                          {h}
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-50">
                    {latestUsers.map((u, i) => {
                      const statusMap = {
                        ACTIVATED:   'bg-green-100 text-green-700',
                        ACTIVE:      'bg-green-100 text-green-700',
                        PENDING:     'bg-yellow-100 text-yellow-700',
                        DEACTIVATED: 'bg-red-100 text-red-700',
                        ON_HOLD:     'bg-orange-100 text-orange-700',
                        SUSPENDED:   'bg-amber-100 text-amber-700',
                        BANNED:      'bg-gray-100 text-gray-600',
                      };

                      // Payment badge: FREETRIAL uses inline styles, others use Tailwind classes
                      const payKey = u.paymentStatus?.toUpperCase();
                      const payTailwind = payKey === 'PAID'   ? 'bg-emerald-100 text-emerald-700'
                                        : payKey === 'UNPAID' ? 'bg-rose-100 text-rose-700'
                                        : '';
                      const payInlineStyle = payKey === 'FREETRIAL' ? freeTrialBadgeCls : null;

                      const statusCls = statusMap[u.actionStatus?.toUpperCase()] || 'bg-gray-100 text-gray-500';

                      return (
                        <tr key={u.id || i} className="hover:bg-indigo-50/30 transition-colors">
                          <td className="px-4 py-3 text-xs text-gray-400">{i + 1}</td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-red-400 to-orange-400 flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                                {(u.organizationName || u.firstName || u.email || '?').charAt(0).toUpperCase()}
                              </div>
                              <div>
                                <p className="text-sm font-semibold text-gray-800 whitespace-nowrap">{u.organizationName || '—'}</p>
                                <p className="text-xs text-gray-400">{u.firstName} {u.lastName}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-600 whitespace-nowrap">{u.email || '—'}</td>
                          <td className="px-4 py-3">
                            <span className="px-2.5 py-0.5 rounded-full text-xs font-medium bg-indigo-50 text-indigo-700 whitespace-nowrap">
                              {u.organizationType || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span
                              className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${payTailwind}`}
                              style={payInlineStyle || {}}
                            >
                              {u.paymentStatus || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${statusCls}`}>
                              {u.actionStatus || '—'}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                            {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '—'}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

        </div>
      )}

      {/* ── Empty state ── */}
      {!loading && !stats && (
        <div className="flex flex-col items-center justify-center p-16 text-center">
          <BarChart3 size={52} className="text-gray-200 mb-4" />
          <p className="text-gray-400 text-sm">No data available. Click Refresh to load statistics.</p>
        </div>
      )}
    </div>
  );
};

export default SuperAdminDashboard;